import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Image, Clock, Navigation, Edit3 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";

interface DeviceLocationManagerProps {
  device: any;
  deviceId: string;
  onUpdate: () => Promise<void>;
}

export const DeviceLocationManager = ({
  device,
  deviceId,
  onUpdate
}: DeviceLocationManagerProps) => {
  const [localName, setLocalName] = useState(device.deviceName || "");
  const [localDescription, setLocalDescription] = useState(device.deviceDescription || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const { logChange } = useDeviceChangeLog();

  // Update local state when device data changes
  useEffect(() => {
    setLocalName(device.deviceName || "");
    setLocalDescription(device.deviceDescription || "");
  }, [device.deviceName, device.deviceDescription]);

  const updateField = async (field: string, value: string) => {
    setIsUpdating(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", field);
      params.append("value", value);
      
      logChange(deviceId, field, value);
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      });

      if (!response.ok) {
        throw new Error("Failed to update device");
      }

      await onUpdate();
      toast.success("Location details updated successfully");
    } catch (error) {
      console.error("Error updating device:", error);
      toast.error("Failed to update location details");
    } finally {
      setIsUpdating(false);
    }
  };

  // Extract sponsor status from device
  const sponsorStatus = device.enableSponsorship === 1 || device.enableSponsorship === "1" ? "Yes" : "No";

  // Extract latest image information
  const latestImage = device.imageKit?.thumbnailUrl || device.imageKit?.url;
  const imageTimestamp = device.deviceLastUpdated;
  
  // Extract GPS coordinates
  const latitude = device.latWgs84 || device.gnss?.latitude;
  const longitude = device.longWgs84 || device.gnss?.longitude;
  const hasGpsLocation = latitude && longitude;

  // Format timestamp
  const formattedTimestamp = imageTimestamp 
    ? formatDistanceToNow(new Date(imageTimestamp), { addSuffix: true })
    : "Unknown";

  return (
    <div className="p-2">
      {/* Header */}
      <div className="flex items-center mb-4">
        <MapPin className="h-5 w-5 text-brand-500 mr-2" />
        <h2 className="text-lg font-semibold">Location Manager</h2>
      </div>

      <div className="space-y-6">
        {/* Section 1: Location Details */}
        <Card className="border bg-muted/50">
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-brand-500" />
              <CardTitle className="text-base">Location Details</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Name Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <div className="flex gap-2">
                <Input 
                  value={localName} 
                  onChange={(e) => setLocalName(e.target.value)}
                  placeholder="Enter location name"
                  className="h-9 text-sm"
                  disabled={isUpdating || !canEditDeviceConfig}
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button 
                          variant="outline" 
                          onClick={() => updateField("deviceName", localName)}
                          className="h-9 text-sm"
                          disabled={isUpdating || !canEditDeviceConfig}
                        >
                          Update
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canEditDeviceConfig && (
                      <TooltipContent>
                        <p>Only administrators can edit device settings</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Description Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <div className="flex gap-2">
                <Textarea 
                  value={localDescription} 
                  onChange={(e) => setLocalDescription(e.target.value)}
                  placeholder="Enter location description"
                  className="min-h-[60px] max-h-[70px] text-sm resize-none"
                  disabled={isUpdating || !canEditDeviceConfig}
                />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button 
                          variant="outline" 
                          onClick={() => updateField("deviceDescription", localDescription)}
                          className="h-9 text-sm"
                          disabled={isUpdating || !canEditDeviceConfig}
                        >
                          Update
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!canEditDeviceConfig && (
                      <TooltipContent>
                        <p>Only administrators can edit device settings</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Sponsor Status */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Sponsor</label>
              <div className="flex items-center">
                <Badge 
                  variant={sponsorStatus === "Yes" ? "default" : "secondary"}
                  className="text-sm"
                >
                  {sponsorStatus}
                </Badge>
                <span className="text-xs text-muted-foreground ml-2">
                  This is controlled in the Configuration tab
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Image Details */}
        <Card className="border bg-muted/50">
          <CardHeader className="py-3">
            <div className="flex items-center gap-2">
              <Image className="h-4 w-4 text-brand-500" />
              <CardTitle className="text-base">Image Details</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Latest Image */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Image className="h-4 w-4" />
                    Latest Image
                  </label>
                  <div className="bg-muted/30 rounded-lg overflow-hidden border">
                    {latestImage ? (
                      <div className="aspect-[4/3] relative">
                        <img 
                          src={latestImage} 
                          alt="Latest device capture"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="aspect-[4/3] flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No image available</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Image Metadata */}
              <div className="space-y-4">
                {/* Timestamp */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Image Timestamp
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg border">
                    <p className="text-sm font-medium">
                      {formattedTimestamp}
                    </p>
                    {imageTimestamp && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(imageTimestamp).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                {/* GPS Location */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Navigation className="h-4 w-4" />
                    GPS Location
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg border">
                    {hasGpsLocation ? (
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {parseFloat(latitude).toFixed(6)}°, {parseFloat(longitude).toFixed(6)}°
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                          <span>Lat: {parseFloat(latitude).toFixed(6)}°</span>
                          <span>Lng: {parseFloat(longitude).toFixed(6)}°</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No GPS coordinates available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};